/**
 * NVIDIA Air Topology Export
 *
 * Exports cluster configuration to NVIDIA Air-compatible JSON topology format
 */

import type { BillOfMaterials } from '../types/bom';
import type { UIConfiguration } from '../types/ui-configuration';

interface AirNode {
  cpu: number;
  memory: number;
  storage: number;
  positioning: {
    x: number;
    y: number;
  };
  os: string;
  features: {
    uefi: boolean;
    tpm: boolean;
  };
  pxehost: boolean;
  secureboot: boolean;
  oob: boolean;
  emulation_type: null;
  network_pci: Record<string, never>;
  cpu_options: never[];
  storage_pci: Record<string, never>;
  nic_model?: string;
}

interface AirLinkEndpoint {
  interface: string;
  node: string;
  mac: string;
  network_pci: null;
  interface_role?: null;
  scalable_unit?: null;
}

type AirLink = [AirLinkEndpoint, AirLinkEndpoint] | [AirLinkEndpoint, string];

interface AirTopology {
  format: 'JSON';
  title: string;
  ztp: string;
  content: {
    nodes: Record<string, AirNode>;
    links: AirLink[];
    oob: boolean;
    netq: boolean;
  };
}

/**
 * Generate a MAC address for a given node and interface
 */
function generateMacAddress(nodeId: string, interfaceId: string): string {
  // Simple hash-based MAC generation for consistency
  const hash = (str: string) => {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = (h << 5) - h + str.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  };

  const combined = `${nodeId}-${interfaceId}`;
  const h = hash(combined);

  // Generate MAC with 48:b0:2d prefix (similar to example)
  const mac = [
    '48', 'b0', '2d',
    ((h >> 16) & 0xff).toString(16).padStart(2, '0'),
    ((h >> 8) & 0xff).toString(16).padStart(2, '0'),
    (h & 0xff).toString(16).padStart(2, '0')
  ];

  return mac.join(':');
}

/**
 * Export cluster configuration to NVIDIA Air topology format
 */
export function exportNvidiaAirTopology(
  bom: BillOfMaterials,
  configuration: UIConfiguration
): AirTopology {
  const nodes: Record<string, AirNode> = {};
  const links: AirLink[] = [];

  // Layout constants for positioning
  const VERTICAL_SPACING = 275;
  const HORIZONTAL_SPACING = 275;
  const START_X = 550;
  const START_Y = 100;

  let currentY = START_Y;

  // Get switch counts from BOM
  const switchBreakdown = bom.summary.network.switchBreakdown;
  const ibLeafCount = switchBreakdown.eastWestLeaf;
  const ibSpineCount = switchBreakdown.eastWestSpine;
  const ethLeafCount = switchBreakdown.northSouthLeaf;
  const ethSpineCount = switchBreakdown.northSouthSpine;
  const oobCount = switchBreakdown.management;

  // Determine OS based on configuration
  const switchOS = 'cumulus-vx-5.8.0'; // Default to Cumulus Linux
  const serverOS = 'generic/ubuntu2204';
  const oobServerOS = 'oob-mgmt-server';
  const oobSwitchOS = 'oob-mgmt-switch';

  // Add OOB management server (if oob enabled)
  if (oobCount > 0) {
    nodes['oob-mgmt-server'] = {
      cpu: 1,
      memory: 1024,
      storage: 10,
      positioning: { x: 275, y: currentY },
      os: oobServerOS,
      features: { uefi: false, tpm: false },
      pxehost: false,
      secureboot: false,
      oob: true,
      emulation_type: null,
      network_pci: {},
      cpu_options: [],
      storage_pci: {}
    };

    // Add OOB management switch
    nodes['oob-mgmt-switch'] = {
      cpu: 1,
      memory: 2048,
      storage: 10,
      positioning: { x: 275, y: currentY + VERTICAL_SPACING },
      os: oobSwitchOS,
      features: { uefi: false, tpm: false },
      pxehost: false,
      secureboot: false,
      oob: true,
      emulation_type: null,
      network_pci: {},
      cpu_options: [],
      storage_pci: {}
    };

    // Link OOB server to OOB switch
    links.push([
      {
        interface: 'eth1',
        node: 'oob-mgmt-server',
        mac: generateMacAddress('oob-mgmt-server', 'eth1'),
        network_pci: null
      },
      {
        interface: 'swp1',
        node: 'oob-mgmt-switch',
        mac: generateMacAddress('oob-mgmt-switch', 'swp1'),
        network_pci: null,
        interface_role: null,
        scalable_unit: null
      }
    ]);

    // Link OOB server to outside world
    links.push([
      {
        interface: 'eth0',
        node: 'oob-mgmt-server',
        mac: generateMacAddress('oob-mgmt-server', 'eth0'),
        network_pci: null
      },
      'outbound'
    ]);

    currentY += VERTICAL_SPACING * 2;
  }

  // Add InfiniBand spine switches
  for (let i = 0; i < ibSpineCount; i++) {
    const nodeId = `ib-spine${i + 1}`;
    nodes[nodeId] = {
      cpu: 2,
      memory: 4096,
      storage: 20,
      positioning: { x: START_X + (i * HORIZONTAL_SPACING), y: currentY },
      os: switchOS,
      features: { uefi: false, tpm: false },
      pxehost: false,
      secureboot: false,
      oob: false,
      emulation_type: null,
      network_pci: {},
      cpu_options: [],
      storage_pci: {}
    };

    // Connect to OOB if present
    if (oobCount > 0) {
      links.push([
        {
          interface: 'eth0',
          node: nodeId,
          mac: generateMacAddress(nodeId, 'eth0'),
          network_pci: null
        },
        {
          interface: `swp${10 + i}`,
          node: 'oob-mgmt-switch',
          mac: generateMacAddress('oob-mgmt-switch', `swp${10 + i}`),
          network_pci: null,
          interface_role: null,
          scalable_unit: null
        }
      ]);
    }
  }

  currentY += VERTICAL_SPACING;

  // Add InfiniBand leaf switches
  for (let i = 0; i < ibLeafCount; i++) {
    const nodeId = `ib-leaf${i + 1}`;
    nodes[nodeId] = {
      cpu: 2,
      memory: 4096,
      storage: 20,
      positioning: { x: START_X + (i * HORIZONTAL_SPACING), y: currentY },
      os: switchOS,
      features: { uefi: false, tpm: false },
      pxehost: false,
      secureboot: false,
      oob: false,
      emulation_type: null,
      network_pci: {},
      cpu_options: [],
      storage_pci: {}
    };

    // Connect to all IB spine switches
    for (let j = 0; j < ibSpineCount; j++) {
      links.push([
        {
          interface: `swp${51 + j}`,
          node: nodeId,
          mac: generateMacAddress(nodeId, `swp${51 + j}`),
          network_pci: null
        },
        {
          interface: `swp${i + 1}`,
          node: `ib-spine${j + 1}`,
          mac: generateMacAddress(`ib-spine${j + 1}`, `swp${i + 1}`),
          network_pci: null,
          interface_role: null,
          scalable_unit: null
        }
      ]);
    }

    // Connect to OOB if present
    if (oobCount > 0) {
      links.push([
        {
          interface: 'eth0',
          node: nodeId,
          mac: generateMacAddress(nodeId, 'eth0'),
          network_pci: null
        },
        {
          interface: `swp${20 + i}`,
          node: 'oob-mgmt-switch',
          mac: generateMacAddress('oob-mgmt-switch', `swp${20 + i}`),
          network_pci: null,
          interface_role: null,
          scalable_unit: null
        }
      ]);
    }

    // Add MLAG/peer links between leaf pairs
    if (i % 2 === 0 && i + 1 < ibLeafCount) {
      const peerNodeId = `ib-leaf${i + 2}`;
      links.push([
        {
          interface: 'swp49',
          node: nodeId,
          mac: generateMacAddress(nodeId, 'swp49'),
          network_pci: null
        },
        {
          interface: 'swp49',
          node: peerNodeId,
          mac: generateMacAddress(peerNodeId, 'swp49'),
          network_pci: null,
          interface_role: null,
          scalable_unit: null
        }
      ]);
      links.push([
        {
          interface: 'swp50',
          node: nodeId,
          mac: generateMacAddress(nodeId, 'swp50'),
          network_pci: null
        },
        {
          interface: 'swp50',
          node: peerNodeId,
          mac: generateMacAddress(peerNodeId, 'swp50'),
          network_pci: null,
          interface_role: null,
          scalable_unit: null
        }
      ]);
    }
  }

  currentY += VERTICAL_SPACING;

  // Add Ethernet spine switches if enabled
  if (ethSpineCount > 0) {
    for (let i = 0; i < ethSpineCount; i++) {
      const nodeId = `eth-spine${i + 1}`;
      nodes[nodeId] = {
        cpu: 2,
        memory: 4096,
        storage: 20,
        positioning: { x: START_X + 1000 + (i * HORIZONTAL_SPACING), y: currentY },
        os: switchOS,
        features: { uefi: false, tpm: false },
        pxehost: false,
        secureboot: false,
        oob: false,
        emulation_type: null,
        network_pci: {},
        cpu_options: [],
        storage_pci: {}
      };

      // Connect to OOB if present
      if (oobCount > 0) {
        links.push([
          {
            interface: 'eth0',
            node: nodeId,
            mac: generateMacAddress(nodeId, 'eth0'),
            network_pci: null
          },
          {
            interface: `swp${200 + i}`,
            node: 'oob-mgmt-switch',
            mac: generateMacAddress('oob-mgmt-switch', `swp${200 + i}`),
            network_pci: null,
            interface_role: null,
            scalable_unit: null
          }
        ]);
      }
    }

    currentY += VERTICAL_SPACING;
  }

  // Add Ethernet leaf switches if enabled
  if (ethLeafCount > 0) {
    for (let i = 0; i < ethLeafCount; i++) {
      const nodeId = `eth-leaf${i + 1}`;
      nodes[nodeId] = {
        cpu: 2,
        memory: 4096,
        storage: 20,
        positioning: { x: START_X + 1000 + (i * HORIZONTAL_SPACING), y: currentY },
        os: switchOS,
        features: { uefi: false, tpm: false },
        pxehost: false,
        secureboot: false,
        oob: false,
        emulation_type: null,
        network_pci: {},
        cpu_options: [],
        storage_pci: {}
      };

      // Connect to all Ethernet spine switches
      for (let j = 0; j < ethSpineCount; j++) {
        links.push([
          {
            interface: `swp${51 + j}`,
            node: nodeId,
            mac: generateMacAddress(nodeId, `swp${51 + j}`),
            network_pci: null
          },
          {
            interface: `swp${i + 1}`,
            node: `eth-spine${j + 1}`,
            mac: generateMacAddress(`eth-spine${j + 1}`, `swp${i + 1}`),
            network_pci: null,
            interface_role: null,
            scalable_unit: null
          }
        ]);
      }

      // Connect to OOB if present
      if (oobCount > 0) {
        links.push([
          {
            interface: 'eth0',
            node: nodeId,
            mac: generateMacAddress(nodeId, 'eth0'),
            network_pci: null
          },
          {
            interface: `swp${210 + i}`,
            node: 'oob-mgmt-switch',
            mac: generateMacAddress('oob-mgmt-switch', `swp${210 + i}`),
            network_pci: null,
            interface_role: null,
            scalable_unit: null
          }
        ]);
      }

      // Add MLAG/peer links between leaf pairs
      if (i % 2 === 0 && i + 1 < ethLeafCount) {
        const peerNodeId = `eth-leaf${i + 2}`;
        links.push([
          {
            interface: 'swp49',
            node: nodeId,
            mac: generateMacAddress(nodeId, 'swp49'),
            network_pci: null
          },
          {
            interface: 'swp49',
            node: peerNodeId,
            mac: generateMacAddress(peerNodeId, 'swp49'),
            network_pci: null,
            interface_role: null,
            scalable_unit: null
          }
        ]);
        links.push([
          {
            interface: 'swp50',
            node: nodeId,
            mac: generateMacAddress(nodeId, 'swp50'),
            network_pci: null
          },
          {
            interface: 'swp50',
            node: peerNodeId,
            mac: generateMacAddress(peerNodeId, 'swp50'),
            network_pci: null,
            interface_role: null,
            scalable_unit: null
          }
        ]);
      }
    }

    currentY += VERTICAL_SPACING;
  }

  // Get ports per node from configuration
  const ibPortsPerNode = configuration.network.eastWest.portsPerNode;
  const ethPortsPerNode = configuration.network.northSouth.enabled
    ? configuration.network.northSouth.portsPerNode
    : 0;
  const oobPortsPerNode = configuration.network.management.portsPerNode;

  // Limit compute nodes for Air compatibility (Air is designed for smaller topologies)
  // Export representative subset while maintaining full switch topology
  const MAX_SERVERS_FOR_AIR = 64;
  const totalNodes = bom.summary.compute.totalNodes;
  const nodeCount = Math.min(totalNodes, MAX_SERVERS_FOR_AIR);
  const serversPerRow = 10; // For positioning
  const isSubsetExport = totalNodes > MAX_SERVERS_FOR_AIR;

  for (let i = 0; i < nodeCount; i++) {
    const nodeId = `server${(i + 1).toString().padStart(2, '0')}`;
    const row = Math.floor(i / serversPerRow);
    const col = i % serversPerRow;

    nodes[nodeId] = {
      nic_model: 'e1000',
      cpu: 2,
      memory: 8192,
      storage: 100,
      positioning: {
        x: START_X + (col * HORIZONTAL_SPACING),
        y: currentY + (row * VERTICAL_SPACING)
      },
      os: serverOS,
      features: { uefi: false, tpm: false },
      pxehost: false,
      secureboot: false,
      oob: false,
      emulation_type: null,
      network_pci: {},
      cpu_options: [],
      storage_pci: {}
    };

    // Connect InfiniBand ports
    for (let port = 0; port < ibPortsPerNode; port++) {
      const leafIndex = (i * ibPortsPerNode + port) % ibLeafCount;
      const leafNode = `ib-leaf${leafIndex + 1}`;
      const switchPort = Math.floor((i * ibPortsPerNode + port) / ibLeafCount) + 1;

      links.push([
        {
          interface: `swp${switchPort}`,
          node: leafNode,
          mac: generateMacAddress(leafNode, `swp${switchPort}`),
          network_pci: null
        },
        {
          interface: `eth${port + 1}`,
          node: nodeId,
          mac: generateMacAddress(nodeId, `eth${port + 1}`),
          network_pci: null,
          interface_role: null,
          scalable_unit: null
        }
      ]);
    }

    // Connect Ethernet ports if enabled
    if (ethPortsPerNode > 0 && ethLeafCount > 0) {
      for (let port = 0; port < ethPortsPerNode; port++) {
        const leafIndex = (i * ethPortsPerNode + port) % ethLeafCount;
        const leafNode = `eth-leaf${leafIndex + 1}`;
        const switchPort = Math.floor((i * ethPortsPerNode + port) / ethLeafCount) + 1;

        links.push([
          {
            interface: `swp${switchPort}`,
            node: leafNode,
            mac: generateMacAddress(leafNode, `swp${switchPort}`),
            network_pci: null
          },
          {
            interface: `ens${port + 1}`,
            node: nodeId,
            mac: generateMacAddress(nodeId, `ens${port + 1}`),
            network_pci: null,
            interface_role: null,
            scalable_unit: null
          }
        ]);
      }
    }

    // Connect OOB if present
    if (oobCount > 0) {
      const oobSwitchPort = 100 + i; // Start at swp100 to avoid conflicts
      links.push([
        {
          interface: `swp${oobSwitchPort}`,
          node: 'oob-mgmt-switch',
          mac: generateMacAddress('oob-mgmt-switch', `swp${oobSwitchPort}`),
          network_pci: null
        },
        {
          interface: 'eth0',
          node: nodeId,
          mac: generateMacAddress(nodeId, 'eth0'),
          network_pci: null,
          interface_role: null,
          scalable_unit: null
        }
      ]);
    }
  }

  // Generate ZTP script
  const ztpScript = `#!/bin/bash
# GPU Cluster BOM Calculator - NVIDIA Air Topology
# Generated on ${new Date().toISOString()}
# Cluster: ${configuration.scaling.totalGPUs} GPUs / ${totalNodes} nodes${isSubsetExport ? ` (showing ${nodeCount}/${totalNodes} representative servers)` : ''}

function error() {
  echo -e "\\e[0;33mERROR: The Zero Touch Provisioning script failed while running the command $BASH_COMMAND at line $BASH_LINENO.\\e[0m" >&2
}
trap error ERR

SSH_URL="http://192.168.200.1/authorized_keys"
mkdir -p /home/cumulus/.ssh
wget -O /home/cumulus/.ssh/authorized_keys $SSH_URL

passwd -x 99999 cumulus
echo 'cumulus:CumulusLinux!' | chpasswd

echo "cumulus ALL=(ALL) NOPASSWD:ALL" > /etc/sudoers.d/10_cumulus

exit 0
#CUMULUS-AUTOPROVISIONING`;

  return {
    format: 'JSON',
    title: `GPU Cluster - ${configuration.scaling.totalGPUs} GPUs${isSubsetExport ? ` (${nodeCount}/${totalNodes} servers)` : ''}`,
    ztp: ztpScript,
    content: {
      nodes,
      links,
      oob: false,
      netq: false
    }
  };
}
